<template>
    <section class="home-hero" :style="{ backgroundImage: `url(${currentSlide.image})` }" @mouseenter="stopAutoPlay"
        @mouseleave="startAutoPlay">
        <div class="home-hero__content">
            <div class="home-hero__text">
                <a class="home-hero__title-link" :href="getPageUrl('/cities')">
                    <h1 class="home-hero__title">{{ heroTitle }}</h1>
                </a>
                <p class="home-hero__subtitle">{{ heroSubtitle }}</p>
                <a class="home-hero__btn" :href="getPageUrl('/collections')">
                    <img class="home-hero__btn-icon" src="@/assets/collection-star.svg" alt="Collections">
                    <span>Collections</span>
                </a>
                <div v-if="mobileCollectionsAd" class="home-hero__collections-ad">
                    <AdComponent class="ad-width-100" :ads="mobileCollectionsAd" :showDebug="false"
                        :forceDebug="true" debugLabel="home_1"
                        debugPosition="Collections 按钮下方（仅移动端）" />
                </div>
            </div>
            <div class="home-hero__quote">
                <p>"{{ currentSlide.text }}"</p>
                <cite>— {{ currentSlide.author }}</cite>
                <div class="home-hero__dots">
                    <button v-for="(slide, index) in heroSlides" :key="slide.id" type="button"
                        :class="{ active: currentIndex === index }" @click="setSlide(index)"></button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { mapGetters } from 'vuex'
import AdComponent from '@/pages/components/AdComponent.vue'

export default {
    name: 'HomeHero',
    components: {
        AdComponent
    },
    props: {
        mobileCollectionsAd: {
            type: Object,
            default: () => null
        }
    },
    data() {
        return {
            currentIndex: 0,
            timer: null
        }
    },
    computed: {
        ...mapGetters(['livableCityData', 'host']),
        heroTitle() {
            return 'LIVABLE CITIES'
        },
        heroSubtitle() {
            return '50 Livable Cities Around the World'
        },
        heroSlides() {
            const quotes = this.livableCityData.quotes || []
            if (quotes.length >= 3) {
                const siteSeed = this.hashSeed(this.host || this.$route.params.channel || 'default')
                const sorted = quotes
                    .map((item, index) => ({
                        item,
                        rank: this.hashSeed(`${siteSeed}-${item?.id || item?.text || index}`),
                    }))
                    .sort((a, b) => a.rank - b.rank)
                    .slice(0, 3)
                    .map((entry) => entry.item)

                return sorted.map((item) => ({
                    id: item.id,
                    image: item.image,
                    text: item.text,
                    author: item.author
                }))
            }
            return this.livableCityData.homeHeroSlides || []
        },
        currentSlide() {
            return this.heroSlides[this.currentIndex] || {
                image: require('@/assets/default/hero-bg.jpg'),
                text: 'Discover your next city with data-backed livability insights.',
                author: 'City Editorial'
            }
        }
    },
    mounted() {
        this.startAutoPlay()
    },
    beforeDestroy() {
        this.stopAutoPlay()
    },
    methods: {
        startAutoPlay() {
            this.stopAutoPlay()
            if (this.heroSlides.length < 2) {
                return
            }
            this.timer = setInterval(() => {
                this.currentIndex = (this.currentIndex + 1) % this.heroSlides.length
            }, 5000)
        },
        stopAutoPlay() {
            if (!this.timer) {
                return
            }
            clearInterval(this.timer)
            this.timer = null
        },
        setSlide(index) {
            this.currentIndex = index
            this.startAutoPlay()
        },
        hashSeed(value) {
            return String(value || '').split('').reduce((acc, ch) => {
                return (acc * 31 + ch.charCodeAt(0)) % 1000003
            }, 7)
        },
        getPageUrl(path) {
            if (this.$route.params.channel) {
                return `/${this.$route.params.channel}${path}`;
            }
            return path;
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.home-hero {
    position: relative;
    height: rem(810);
    width: 100%;
    box-sizing: border-box;
    background-image: url('~@/assets/default/hero-bg.jpg');
    background-size: cover;
    background-position: center;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.3);
    }

    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: rem(220);
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 50%, rgba(0, 0, 0, 0.6) 100%);
    }

    &__content {
        position: relative;
        z-index: 1;
        height: 100%;
        display: flex;
        flex-direction: column;
        color: #fff;
    }

    &__text {
        padding-top: rem(180);
        display: flex;
        flex: 1;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    &__title {
        margin: 0;
        font-family: $font-family-serif;
        font-size: rem(52);
        font-weight: 700;
        line-height: rem(60);
        text-decoration: underline;
        text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);
        transition: transform 0.25s ease;
        cursor: pointer;

        &:hover {
            transform: translateY(rem(-6));
        }
    }

    &__title-link {
        text-decoration: none;
        color: inherit;
    }

    &__subtitle {
        margin: rem(24) 0 rem(40);
        font-family: $font-family-serif;
        font-size: rem(28);
        font-weight: 500;
        line-height: rem(36);
        text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);
    }

    &__btn {
        width: rem(220);
        height: rem(60);
        border-radius: rem(100);
        background: $color-primary;
        color: #000;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: rem(12);
        text-decoration: none;
        font-family: $font-family-serif;
        font-size: rem(22);
        font-weight: 700;
        box-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);
        transition: transform 0.25s ease;
        cursor: pointer;

        &:hover {
            transform: translateY(rem(-6));
        }
    }

    &__btn-icon {
        width: rem(24);
        height: rem(24);
    }

    &__collections-ad {
        margin-top: rem(20);
        width: 100%;
        padding: 0 rem(16);
        box-sizing: border-box;
    }

    &__quote {
        margin-top: auto;
        padding-bottom: rem(60);
        font-family: $font-family-serif;
        font-size: rem(18);
        font-style: italic;
        line-height: rem(28);
        text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);

        p,
        cite {
            margin: 0;
            display: block;
        }
    }

    &__dots {
        margin-top: rem(24);
        display: flex;
        gap: rem(8);

        button {
            width: rem(8);
            height: rem(8);
            border: 0;
            padding: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.4);
            cursor: pointer;

            &.active {
                background: #fff;
            }
        }
    }
}

.detect_deviece_pc,
.detect_deviece_desktop {
    .home-hero {
        &__quote {
            padding-left: rem(180);
            padding-right: rem(180);
            max-width: rem(1080);
        }
    }
}

.detect_deviece_mobile {
    .home-hero {
        &__text {
            padding: rem(120) rem(16) 0;
        }

        &__quote {
            padding-left: rem(16);
            padding-right: rem(16);
        }
    }
}
</style>
